import React, { useState } from 'react';
import axios from 'axios';
import Button from './Button';
import { useToken } from '../../hooks/useToken';
import Spinner from '../common/Spinner';
import { getConfig } from '../../config/config-helper';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { fetchEventDetails, selectRequestStatus } from '../../reducers/eventsSlice';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { validateEmail } from '../../utilities/validation';

interface Props {
  eventSlug: string;
  size?: 'medium' | 'large';
  className?: string;
}

export default function AddInstructorButton({ eventSlug, size = 'medium', className = '' }: Props) {
  const dispatch = useAppDispatch();
  const token = useToken();
  const { canPublish } = useCurrentUser();
  const role = canPublish ? 'admin' : 'consumer';
  const [disabled, setDisabled] = useState(false);
  const [adding, setAdding] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [invalidEmails, setInvalidEmails] = useState<string[]>([]);
  const [showEmailInput, setShowEmailInput] = useState(false);
  const [emailsInput, setEmailsInput] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const requestStatus = useAppSelector(selectRequestStatus);

  const { dataopsAdminAppApiEndpoint } = getConfig();

  async function addAttendeeClickHandler() {
    if (!showEmailInput) {
      setShowEmailInput(true);
      return;
    }

    if (!emailsInput.trim()) {
      setErrors(['Please enter at least one email address']);
      return;
    }

    // Parse emails from input (split by commas, semicolons, or newlines)
    const emailList = emailsInput
      .split(/[,;\n]/)
      .map((email) => email.trim())
      .filter((email) => email !== '');

    if (emailList.length === 0) {
      setErrors(['Please enter valid email addresses']);
      return;
    }

    // Client-side email validation
    const invalidEmailsList = emailList.filter((email) => !validateEmail(email));
    if (invalidEmailsList.length > 0) {
      setInvalidEmails(invalidEmailsList);
      setErrors([`The following emails are invalid: ${invalidEmailsList.join(', ')}`]);
      return;
    }

    setDisabled(true);
    setAdding(true);
    setErrors([]);
    setInvalidEmails([]);
    setSuccessMessage('');

    const url = `${dataopsAdminAppApiEndpoint}/event_management/${eventSlug}/instructors`;
    try {
      await axios.post(
        url,
        { instructors: emailList },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      dispatch(fetchEventDetails({ token, role, slug: eventSlug })).catch((err: unknown) => err);
      setEmailsInput('');
      setShowEmailInput(false);
      setSuccessMessage('Instructors added successfully');
      setTimeout(() => {
        setSuccessMessage('');
      }, 10000);
    } catch (err: any) {
      // Using any here for compatibility with axios error responses
      console.log('Failed to add instructors: ', err);

      // Get the message_code from the API response
      const messageCode = err.response?.data?.message_code;
      let errorMessage = err.response?.data?.message || 'Failed to add instructors';

      console.log('Error message code:', messageCode);
      // Customize error message based on message_code
      switch (messageCode) {
        case 'ERR_USER_NOT_MEMBER':
          errorMessage = 'You are not registered as a member in the system.';
          break;
        case 'ERR_EVENT_NOT_FOUND':
          errorMessage = 'The specified event could not be found.';
          break;
        case 'ERR_INTERNAL_SERVER_ERROR':
          errorMessage = 'An internal server error occurred. Please try again later.';
          break;
        case 'ERR_NOT_AUTHORIZED':
          errorMessage = 'You are not authorized to add instructors to this event.';
          break;
        case 'ERR_INVALID_PAYLOAD':
          errorMessage = 'Invalid input format. Please provide valid email addresses.';
          break;
        case 'ERR_INVALID_EMAIL_FORMAT':
          if (err.response?.data?.invalid_emails && Array.isArray(err.response.data.invalid_emails)) {
            const invalidEmails = err.response.data.invalid_emails as string[];
            errorMessage = `The following emails are invalid: ${invalidEmails.join(', ')}`;
          } else {
            errorMessage = 'One or more email addresses are not in a valid format.';
          }
          break;
        case 'ERR_MEMBERS_NOT_FOUND':
          if (err.response?.data?.invalid_emails && Array.isArray(err.response.data.invalid_emails)) {
            const invalidEmails = err.response.data.invalid_emails as string[];
            errorMessage = `The following users are not registered in the system: ${invalidEmails.join(', ')}`;
          } else {
            errorMessage = 'One or more users are not registered in the system.';
          }
          break;
        default:
          // Use the original message from the server
          break;
      }
      if (err.code === 'ERR_NETWORK') {
        errorMessage = 'Network error. Please check your internet connection.';
      }
      setErrors([errorMessage]);
    } finally {
      setDisabled(false);
      setAdding(false);
    }
  }

  const handleCancel = () => {
    setShowEmailInput(false);
    setEmailsInput('');
    setErrors([]);
    setInvalidEmails([]);
    setSuccessMessage('');
  };

  const buttonText = adding ? 'Adding...' : showEmailInput ? 'Add' : 'Add Instructor';

  return (
    <div className="flex flex-col">
      {showEmailInput && (
        <div
          className="mb-2 p-2 bg-gray-50 rounded border border-gray-200"
          data-testid="instructor-email-input-container"
        >
          <label className="block text-sm font-medium text-gray-700 mb-1">Enter instructor email addresses:</label>
          <textarea
            className={`w-full p-2 border ${
              invalidEmails.length > 0 ? 'border-red-500 bg-red-50' : 'border-gray-300'
            } rounded text-sm mb-2`}
            rows={3}
            placeholder="Enter email addresses (separated by commas, semicolons, or new lines)"
            value={emailsInput}
            onChange={(e) => {
              setEmailsInput(e.target.value);
              // Clear validation errors when user starts typing again
              if (errors.length > 0 || invalidEmails.length > 0) {
                setErrors([]);
                setInvalidEmails([]);
              }
            }}
            data-testid="instructor-email-input"
          />
          <div className="flex justify-between">
            <button
              className="text-sm text-gray-600 hover:text-gray-800"
              onClick={handleCancel}
              disabled={adding || requestStatus === 'loading'}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <Button
        intent={'light'}
        size={size}
        className={`z-10 ${className}`}
        onClick={() => {
          addAttendeeClickHandler().catch(() => {
            console.error('Error adding instructor:', eventSlug);
          });
        }}
        disabled={disabled || adding || requestStatus === 'loading'}
        data-testid="add-instructor-button"
      >
        {adding ? <Spinner size="small" className="mr-1" /> : ''}
        {buttonText}
      </Button>

      {errors.length > 0 && (
        <div className="text-xs text-red-600 mt-1" data-testid="instructor-error-message">
          {errors.map((error, index) => (
            <div key={index}>{error}</div>
          ))}
        </div>
      )}

      {successMessage && (
        <div className="text-xs text-green-600 mt-1" data-testid="instructor-success-message">
          {successMessage}
        </div>
      )}
    </div>
  );
}
